import { useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { motion, AnimatePresence } from 'framer-motion'
import { HiLocationMarker, HiCalendar, HiChevronDown } from 'react-icons/hi'
import type { Language } from '../App'
import { t } from '../utils/translations'
import { experiences } from '../utils/data'
import './Experience.css'

interface ExperienceProps { language: Language }

export default function Experience({ language }: ExperienceProps) {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true })
  const [expanded, setExpanded] = useState<number | null>(0)

  return (
    <section id="experience" ref={ref}>
      <div className="orb orb-purple exp-orb" />
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ y: 30, opacity: 0 }}
          animate={inView ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.6 }}
        >
          <p className="section-label">{t('exp.label', language)}</p>
          <h2 className="section-title">{t('exp.title', language)}</h2>
          <div className="gradient-divider" />
          <p className="section-desc">{t('exp.desc', language)}</p>
        </motion.div>

        <div className="timeline">
          {experiences.map((exp, i) => (
            <motion.div
              key={exp.id}
              className={`timeline-item ${expanded === i ? 'expanded' : ''}`}
              initial={{ x: -50, opacity: 0 }}
              animate={inView ? { x: 0, opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: i * 0.15 }}
            >
              <div className="timeline-spine">
                <div className="timeline-dot" />
                {i < experiences.length - 1 && <div className="timeline-line" />}
              </div>

              <div className="timeline-card card" onClick={() => setExpanded(expanded === i ? null : i)}>
                <div className="timeline-header">
                  <div className="timeline-title-group">
                    <h3 className="exp-role">{exp.role}</h3>
                    <p className="exp-company">{exp.company}</p>
                  </div>
                  <div className="timeline-meta">
                    <span className="exp-type tag">{exp.type}</span>
                    <motion.span
                      animate={{ rotate: expanded === i ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="exp-chevron"
                    >
                      <HiChevronDown />
                    </motion.span>
                  </div>
                </div>

                <div className="timeline-info">
                  <span className="info-item"><HiCalendar /> {exp.period}</span>
                  <span className="info-item"><HiLocationMarker /> {exp.location}</span>
                </div>

                <AnimatePresence>
                  {expanded === i && (
                    <motion.div
                      className="timeline-details"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35 }}
                    >
                      <p className="exp-desc">{exp.description}</p>
                      <ul className="highlights">
                        {exp.highlights.map((h, hi) => (
                          <motion.li
                            key={hi}
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: hi * 0.08 }}
                          >
                            {h}
                          </motion.li>
                        ))}
                      </ul>
                      <div className="tech-tags">
                        {exp.tech.map(tech => (
                          <span key={tech} className="tag">{tech}</span>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
